import React, {useState} from 'react';

import CheckBoxCheckedIcon from './../../../../Icons/CheckBoxCheckedIcon';
import CheckBoxUncheckedIcon from '../../../../Icons/CheckBoxUncheckedIcon';

import useArrayToggler from '../../../../../shared/hooks/useArrayToggler';
import {ArrayTogglerBehaviorTypes} from '../../../../../shared/hooks/useArrayToggler';
import {isDefined} from './../../../../../types/type-guards';

import * as S from './CheckBoxList.styles';

interface ICheckBoxList<T extends unknown, K extends keyof T> {
  name: string;
  type: keyof typeof ArrayTogglerBehaviorTypes;
  data: T[];
  lookup: (item: T) => T[K] | T;
  renderItem: (item: T) => JSX.Element;
  actionHandler: (data: T[]) => void;
  resetHandlerProp?: () => void;
  defaultState?: T[];
  isSingleFilter?: boolean;
  maxLength?: number;
  contentsWrapperStyles?: {
    [K: string]: string; // 🐨 make it take only css properties
  };
}

function CheckBoxList<T extends unknown, K extends keyof T>(props: ICheckBoxList<T, K>) {
  const {
    name,
    type,
    data,
    lookup,
    renderItem,
    actionHandler,
    resetHandlerProp,
    defaultState,
    isSingleFilter = false,
    maxLength,
    contentsWrapperStyles = {},
  } = props;
  const [showMore, setShowMore] = useState(false);
  const [isChecked, onClickHandler, resetHandler] = useArrayToggler(
    data,
    lookup,
    type,
    actionHandler,
    defaultState,
  );

  const hasAnyItemSelected = data.find(item => isChecked(item));

  return (
    <>
      <S.CheckboxListWrapperDiv isSingleFilter={isSingleFilter}>
        {(!isSingleFilter && (
          <S.CheckBoxListHeaderDiv>
            <S.TextCenteredDiv>
              <S.StyledH5>{name}</S.StyledH5>
            </S.TextCenteredDiv>
            <S.FlexColumnDiv
              onClick={() => {
                if (hasAnyItemSelected) {
                  resetHandler();
                  if (isDefined(resetHandlerProp)) resetHandlerProp();
                }
              }}
            >
              <S.ResetTextWrapper isactive={hasAnyItemSelected ? 'true' : 'false'}>
                RESET
              </S.ResetTextWrapper>
            </S.FlexColumnDiv>
          </S.CheckBoxListHeaderDiv>
        )) ||
          null}

        {data.map((item, index) => {
            if (!item) return;
            if (!showMore && maxLength && index > maxLength - 1) return;
            return (
              <S.CheckBoxListRowDiv key={name + String(index)}>
                <S.TextCenteredDiv onClick={() => onClickHandler(item)} {...contentsWrapperStyles}>
                  {(isChecked(item) && <CheckBoxCheckedIcon height="1.6rem" width="1.6rem" />) || (
                    <CheckBoxUncheckedIcon height="1.6rem" width="1.6rem" />
                  )}
                  <S.CheckBoxListText selected={isChecked(item)}>{renderItem(item)}</S.CheckBoxListText>
                </S.TextCenteredDiv>
              </S.CheckBoxListRowDiv>
            );
          })}

        {(maxLength && (
          <S.CheckBoxListRowDiv>
            <S.ShowAllText onClick={() => setShowMore(!showMore)}>
              {(!showMore && '+') || '-'} Show {(!showMore && 'All') || 'Less'}
            </S.ShowAllText>
          </S.CheckBoxListRowDiv>
        )) ||
          null}
      </S.CheckboxListWrapperDiv>
    </>
  );
}

export default React.memo(CheckBoxList);
